/**
 * Cloudflare Turnstile — proof-of-human for the logged-out web capture flow
 * (BK-153-1, be#153). Blocks backend BK-153-2 (challenge → scoped-token
 * exchange); the wire contract is in HANDOFF-backend-turnstile-contract.md.
 *
 * FAILURE POSTURE — decided, not incidental:
 *
 *   Turnstile-down (script blocked, widget errors, no site key, timeout)
 *     → FAIL-OPEN on the client. We submit with no challenge token and let
 *       the backend decide. A privacy extension blocking challenges.cloudflare.com
 *       is indistinguishable from Cloudflare being down, and both are common;
 *       hard-failing here would silently zero out the top of the funnel for a
 *       measurable slice of real people. The abuse ceiling is still held by the
 *       existing anonymous 3-link/day device limit, and the backend is expected
 *       to apply a *stricter* unverified-tier limit when the header is absent.
 *       Every fail-open is tracked (`turnstile_unavailable`) so a spike is
 *       visible rather than silent.
 *
 *   Risk-service-down (backend has the token but cannot verify it: 5xx, or
 *   a `challenge_unavailable` code)
 *     → FAIL-CLOSED-WITH-RETRY on the client, and the *backend* owns whether
 *       the request proceeds. The client never decides that an unverifiable
 *       token is good. It refreshes the widget and offers one retry, because
 *       a Turnstile token is single-use and a stale one would fail again.
 *
 *   Challenge rejected (`challenge_failed` / `challenge_required`, 403)
 *     → FAIL-CLOSED. Reset the widget and ask for a fresh challenge. No retry
 *       loop: a rejected human gets one clean re-attempt, not an infinite spin.
 */

export const TURNSTILE_HEADER = "X-Challenge-Token";
export const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** How long we wait for the script + a token before giving up and failing open. */
export const TURNSTILE_TIMEOUT_MS = 8_000;

export type TurnstileStatus =
  | "disabled" // No site key configured — the challenge is off entirely.
  | "loading"
  | "ready"
  | "solved"
  | "unavailable" // Script or widget failed. Fail-open.
  | "expired";

export type TurnstileWidget = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
      appearance?: "always" | "execute" | "interaction-only";
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
};

declare global {
  interface Window {
    turnstile?: TurnstileWidget;
  }
}

export function getTurnstileSiteKey(): string {
  const key = import.meta.env?.VITE_TURNSTILE_SITE_KEY;
  return typeof key === "string" ? key.trim() : "";
}

/** Turnstile is optional: with no site key the flow runs unchallenged. */
export function turnstileEnabled(): boolean {
  return getTurnstileSiteKey().length > 0;
}

let scriptPromise: Promise<TurnstileWidget | null> | null = null;

/** Resets module state. Tests only. */
export function resetTurnstileLoaderForTests(): void {
  scriptPromise = null;
}

/**
 * Loads the Turnstile script. Resolves with `null` rather than rejecting when
 * the script cannot load — an unreachable challenge is a fail-open condition,
 * not an exception the caller should have to catch.
 */
export function loadTurnstile(timeoutMs = TURNSTILE_TIMEOUT_MS): Promise<TurnstileWidget | null> {
  if (!turnstileEnabled()) return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileWidget | null>((resolve) => {
    let settled = false;
    const finish = (value: TurnstileWidget | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      // A failed load must not be cached as permanent: the next submit retries.
      if (!value) scriptPromise = null;
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`,
    );
    const script = existing ?? document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => finish(window.turnstile ?? null));
    script.addEventListener("error", () => finish(null));
    if (!existing) document.head.appendChild(script);
  });

  return scriptPromise;
}
